import { apiClient } from '@/api/client'
import { endpoints } from '@/api/endpoints'
import type { ApiResponse } from '@/api/types'
import type { SalesOrderRow } from '@/modules/inventory/sales-orders/types'

export type SalesOrderMasterResponse = {
  id: number
  quotCode: string
  code: string
  customerName: string
  warehouseName: string
  reference: string
  orderDate: string
  estDeliveryDate: string
  customerId: number
  paymentMethod: number
  paymentTerm: number
  taxableAmount: number
  taxAmount: number
  discount: number
  total: number
  warehouseId: number
  orderStatus: number
  isDeliveryChallan: boolean
  salesPersonId: number
  attachmentPath: string
  createdDate: string
  createdBy: number
  modifiedDate: string
  modifiedBy: number
  branchId: number
}

function normalizeMaster(raw: any): SalesOrderMasterResponse {
  const dateOnly = (v: unknown) => {
    const s = String(v ?? '')
    return s.includes('T') ? s.split('T')[0] : s
  }
  const dateTime = (v: unknown) => {
    const s = String(v ?? '')
    if (!s) return new Date().toISOString()
    return s.includes('T') ? s : `${s}T00:00:00.000Z`
  }
  return {
    id: Number(raw?.id ?? raw?.Id ?? 0),
    quotCode: String(raw?.quotCode ?? raw?.QuotCode ?? ''),
    code: String(raw?.code ?? raw?.Code ?? ''),
    customerName: String(raw?.customerName ?? raw?.CustomerName ?? ''),
    warehouseName: String(raw?.warehouseName ?? raw?.WarehouseName ?? ''),
    reference: String(raw?.reference ?? raw?.Reference ?? ''),
    orderDate: dateOnly(raw?.orderDate ?? raw?.OrderDate),
    estDeliveryDate: dateOnly(raw?.estDeliveryDate ?? raw?.EstDeliveryDate),
    customerId: Number(raw?.customerId ?? raw?.CustomerId ?? 0),
    paymentMethod: Number(raw?.paymentMethod ?? raw?.PaymentMethod ?? 0),
    paymentTerm: Number(raw?.paymentTerm ?? raw?.PaymentTerm ?? 0),
    taxableAmount: Number(raw?.taxableAmount ?? raw?.TaxableAmount ?? 0),
    taxAmount: Number(raw?.taxAmount ?? raw?.TaxAmount ?? 0),
    discount: Number(raw?.discount ?? raw?.Discount ?? 0),
    total: Number(raw?.total ?? raw?.Total ?? 0),
    warehouseId: Number(raw?.warehouseId ?? raw?.WarehouseId ?? 0),
    orderStatus: Number(raw?.orderStatus ?? raw?.OrderStatus ?? 0),
    isDeliveryChallan: Boolean(raw?.isDeliveryChallan ?? raw?.IsDeliveryChallan ?? false),
    salesPersonId: Number(raw?.salesPersonId ?? raw?.SalesPersonId ?? raw?.salsePersonId ?? 0),
    attachmentPath: String(raw?.attachmentPath ?? raw?.AttachmentPath ?? ''),
    createdDate: dateTime(raw?.createdDate ?? raw?.CreatedDate),
    createdBy: Number(raw?.createdBy ?? raw?.CreatedBy ?? 0),
    modifiedDate: dateTime(raw?.modifiedDate ?? raw?.ModifiedDate),
    modifiedBy: Number(raw?.modifiedBy ?? raw?.ModifiedBy ?? 0),
    branchId: Number(raw?.branchId ?? raw?.BranchId ?? 0),
  }
}

function unwrap(payload: any) {
  if (payload && typeof payload === 'object') {
    if ('success' in payload && 'data' in payload) return payload.data
    if ('data' in payload) return payload.data
  }
  return payload
}

function toArray(payload: any): any[] {
  const raw = unwrap(payload)
  if (Array.isArray(raw)) return raw
  if (raw && typeof raw === 'object' && Array.isArray(raw.data)) return raw.data
  // Some endpoints return a single object instead of an array.
  if (raw && typeof raw === 'object') return [raw]
  return []
}

function mapSalesOrderListRow(x: any): SalesOrderRow {
  const orderDate = String(x?.orderDate ?? x?.OrderDate ?? '')
  const estDate = String(x?.estDeliveryDate ?? x?.EstDeliveryDate ?? '')
  return {
    id: Number(x?.id ?? x?.Id ?? 0),
    code: String(x?.code ?? x?.Code ?? ''),
    reference: String(x?.reference ?? x?.Reference ?? ''),
    warehouse: String(x?.warehouseName ?? x?.WarehouseName ?? ''),
    orderDate: orderDate.includes('T') ? orderDate.split('T')[0] : orderDate,
    expectedDeliveryDate: estDate.includes('T') ? estDate.split('T')[0] : estDate,
    total: Number(x?.total ?? x?.Total ?? 0),
    isDcRequired: Boolean(
      x?.isDeliveryChallan ?? x?.IsDeliveryChallan ?? x?.isDcRequired ?? x?.IsDcRequired ?? false,
    ),
    isInvoiceCreated: Number(x?.orderStatus ?? x?.OrderStatus ?? 0) === 4,
    isDcCreated: Number(x?.orderStatus ?? x?.OrderStatus ?? 0) === 5,
  }
}

export type Option = { id: number; name: string }

export type SalesItemOption = {
  id: number
  name: string
  unitSellingPrice: number
  unitCostPrice: number
  sellingTax: number
}

export type TaxRateOption = { id: number; name: string; percent: number; isDefault?: boolean }

export type DiscountPackage = {
  discountId: number
  discount: number
  code: string
  schemeStartDate: string
  schemeEndDate: string
  itemId: number
  discountPercent: number
}

export type BonusPackage = {
  bonusLineId: number
  bonusId: number
  code: string
  bonusItemId: number
  bonusItemName?: string
  bonusBatchId?: number
  eligibleBonusQuantity: number
  availableBonusStock: number
}

export type SalesOrderBonusLineDto = {
  id: number
  orderId: number
  orderLineId: number
  bonusSchemeId: number
  itemId: number
  itemName?: string
  batchId: number
  quantity: number
}

/** One line inside `salesOrderLinesJson` (multipart string). `tax` = tax rate id. */
export type SalesOrderLineJson = {
  taxAmount: number
  discountPercent: number
  unitPrice: number
  orderId: number
  quantity: number
  tax: number
  lineAmount: number
  itemId: number
  isDeleted: string
  batchId: number
  unitCostPrice: number
  discountAmount: number
  taxPercent: number
  id: number
  taxableAmount: number
}

export type SalesOrderSaveLine = SalesOrderLineJson

export type SalesOrderSavePayload = {
  id: number
  quotCode: string
  code: string
  customerName: string
  warehouseName: string
  reference: string
  orderDate: string
  estDeliveryDate: string
  customerId: number
  paymentMethod: number
  taxableAmount: number
  taxAmount: number
  discount: number
  total: number
  salesPersonId: number
  warehouseId: number
  paymentTerm: number
  orderStatus: number
  isDeliveryChallan: boolean
  attachmentPath: string
  createdDate: string
  createdBy: number
  modifiedDate: string
  modifiedBy: number
  branchId: number
  attachmentFile?: File | null
  /** Built in the UI; serialized to `salesOrderLinesJson` on the wire. */
  itemList: SalesOrderSaveLine[]
}

export class SalesOrderService {
  async getSalesOrderById(id: number): Promise<SalesOrderMasterResponse> {
    const response = await apiClient.get<ApiResponse<any> | any>(endpoints.salesOrders.getSalesOrderById(id))
    const raw = unwrap(response.data)
    return normalizeMaster(raw)
  }

  async getSalesOrderLinesByOrderId(orderId: number): Promise<any[]> {
    const response = await apiClient.get<ApiResponse<any> | any>(
      endpoints.salesOrders.getSalesOrderLinesByOrderId(orderId),
    )
    return toArray(response.data)
  }

  async getAllSalesOrders(
    branchId: number,
    pageSize: number,
    pageNumber: number,
  ): Promise<{ items: SalesOrderRow[]; totalCount: number }> {
    const response = await apiClient.get<ApiResponse<any> | any>(
      endpoints.salesOrders.getAllSalesOrders(branchId, pageSize, pageNumber),
    )
    const payload = response.data
    const top = payload && typeof payload === 'object' && !Array.isArray(payload) ? (payload as Record<string, unknown>) : null
    const raw = unwrap(payload)

    let list: unknown[] = toArray(raw)
    if (list.length === 0 && raw && typeof raw === 'object' && !Array.isArray(raw)) {
      const r = raw as Record<string, unknown>
      if (Array.isArray(r.items)) list = r.items
    }
    if (list.length === 0 && top && Array.isArray(top.items)) {
      list = top.items as unknown[]
    }

    const totalCount = Number(
      (raw && typeof raw === 'object' && !Array.isArray(raw)
        ? (raw as Record<string, unknown>).totalRecords ?? (raw as Record<string, unknown>).totalCount
        : undefined) ??
        top?.totalRecords ??
        top?.totalCount ??
        list.length,
    )

    const items = list.map((row) => mapSalesOrderListRow(row as Record<string, unknown>))
    const tc = Number.isFinite(totalCount) && totalCount >= 0 ? totalCount : items.length
    return { items, totalCount: tc }
  }

  async getCustomers(branchId: number): Promise<Option[]> {
    const response = await apiClient.get<ApiResponse<any> | any>(endpoints.salesOrders.getAllCustomers(branchId))
    const arr = toArray(response.data)
    return arr.map((x: any) => ({
      id: Number(x?.customerId ?? x?.id ?? 0),
      name: String(x?.customerNameEn ?? x?.customerNameAr ?? x?.customerName ?? x?.name ?? ''),
    }))
  }

  async getWarehouses(branchId: number): Promise<Option[]> {
    const url = endpoints.salesOrders.getWarehouses(branchId)
    console.log('[SalesOrders] Getwarehouse request:', { url, branchId })
    const response = await apiClient.get<ApiResponse<any> | any>(url)
    console.log('[SalesOrders] Getwarehouse raw response:', response.data)
    const arr = toArray(response.data)
    return arr.map((x: any) => ({
      id: Number(x?.id ?? x?.Id ?? x?.warehouseId ?? x?.WarehouseId ?? 0),
      name: String(x?.name ?? x?.warehouseName ?? x?.code ?? ''),
    }))
  }

  async getItems(branchId: number): Promise<SalesItemOption[]> {
    const response = await apiClient.get<ApiResponse<any> | any>(endpoints.salesOrders.getAllItems(branchId))
    const arr = toArray(response.data)
    return arr.map((x: any) => ({
      id: Number(x?.itemId ?? x?.id ?? 0),
      name: String(x?.itemNameEn ?? x?.itemNameAr ?? x?.itemName ?? x?.name ?? ''),
      unitSellingPrice: Number(x?.unitSellingPrice ?? x?.UnitSellingPrice ?? 0),
      unitCostPrice: Number(x?.unitCostPrice ?? x?.UnitCostPrice ?? 0),
      sellingTax: Number(x?.sellingTax ?? x?.SellingTax ?? 0),
    }))
  }

  async getTaxRates(): Promise<TaxRateOption[]> {
    const response = await apiClient.get<ApiResponse<any> | any>(endpoints.salesOrders.getAllTaxRates)
    const arr = toArray(response.data)
    return arr.map((x: any) => ({
      id: Number(x?.taxRateId ?? x?.id ?? 0),
      name: String(x?.taxRateNameEn ?? x?.taxRateNameAr ?? x?.lookupNameEn ?? x?.lookupNameAr ?? 'Tax'),
      percent: Number(x?.taxPercent ?? x?.taxRate ?? x?.percent ?? 0),
      isDefault: Boolean(x?.isDefault ?? x?.IsDefault),
    }))
  }

  async getPaymentTerms(): Promise<Option[]> {
    const response = await apiClient.get<ApiResponse<any> | any>(endpoints.salesOrders.getPaymentTerms)
    const arr = toArray(response.data)
    return arr.map((x: any) => ({
      id: Number(x?.id ?? x?.lookupId ?? 0),
      name: String(x?.lookupNameEn ?? x?.lookupNameAr ?? x?.paymentTermNameEn ?? x?.paymentTermNameAr ?? ''),
    }))
  }

  async getPaymentMethods(): Promise<Option[]> {
    const response = await apiClient.get<ApiResponse<any> | any>(endpoints.salesOrders.getPaymentMethods)
    const arr = toArray(response.data)
    return arr
      .filter((x: any) => {
        const active = x?.isActive ?? x?.IsActive
        return active === 1 || active === true || active === '1'
      })
      .map((x: any) => ({
        id: Number(x?.id ?? x?.Id ?? 0),
        name: String(x?.lookupNameEn ?? x?.lookupNameAr ?? x?.name ?? ''),
      }))
  }

  async getBatches(branchId: number, itemId: number, warehouseId?: number): Promise<Option[]> {
    try {
      const response = await apiClient.get<ApiResponse<any> | any>(
        endpoints.salesOrders.getBatches(branchId, itemId, warehouseId),
      )
      const arr = toArray(response.data)
      return arr
        .map((x: any) => ({
          id: Number(x?.id ?? x?.Id ?? x?.batchId ?? x?.BatchId ?? 0),
          name: String(
            x?.code ??
              x?.Code ??
              x?.reference ??
              x?.Reference ??
              x?.batchCode ??
              x?.BatchCode ??
              `Batch ${x?.id ?? x?.Id ?? x?.batchId ?? ''}`,
          ),
        }))
        .filter((x) => x.id > 0)
    } catch {
      return []
    }
  }

  async getWarehouseItemQuantity(whId: number, batchId: number, itemId: number): Promise<number> {
    const url = endpoints.salesOrders.getWarehouseItems(whId, batchId, itemId)
    // Debug: log request + response to confirm quantity mapping
    console.log('[SalesOrders] GetwarehouseItems request:', { url, whId, batchId, itemId })
    let response: any
    try {
      response = await apiClient.get<ApiResponse<any> | any>(url)
      console.log('[SalesOrders] GetwarehouseItems raw response:', response.data)
    } catch (error) {
      console.log('[SalesOrders] GetwarehouseItems error:', error)
      throw error
    }

    const raw = unwrap(response.data)
    const normalized =
      Array.isArray(raw) ? raw :
      Array.isArray(raw?.data) ? raw.data :
      Array.isArray(raw?.Data) ? raw.Data :
      raw
    const obj = Array.isArray(normalized) ? normalized[0] : normalized
    const qty =
      obj?.availableQuantity ??
      obj?.AvailableQuantity ??
      obj?.availableQty ??
      obj?.AvailableQty ??
      obj?.quantity ??
      obj?.Quantity ??
      obj?.qty ??
      obj?.Qty ??
      0
    return Number(qty || 0)
  }

  async getDiscountPackagePercent(params: {
    itemId: number
    branchId: number
    orderDate: string
    batchId?: number
  }): Promise<number | null> {
    const response = await apiClient.get<ApiResponse<any> | any>(
      endpoints.salesOrders.getDiscountPackages(params),
      { skipHandledLocallyErrorUI: true } as any,
    )
    const arr = toArray(response.data)
    if (arr.length === 0) return null
    const first = arr[0] as any
    const pctRaw = first?.discountPercent ?? first?.DiscountPercent
    const pct = Number(pctRaw)
    if (!Number.isFinite(pct)) return null
    return pct
  }

  async getBonusPackages(params: {
    itemId: number
    branchId: number
    warehouseId: number
    quantity: number
    orderDate: string
    promotionBatchId?: number
  }): Promise<BonusPackage[]> {
    const response = await apiClient.get<ApiResponse<any> | any>(
      endpoints.salesOrders.getBonusPackages(params),
      { skipHandledLocallyErrorUI: true } as any,
    )
    return toArray(response.data)
      .map((row: any): BonusPackage | null => {
        const itemId = Number(row?.bonusItemId ?? row?.BonusItemId ?? 0)
        const qty = Number(row?.eligibleBonusQuantity ?? row?.EligibleBonusQuantity ?? 0)
        if (!itemId || qty <= 0) return null
        return {
          bonusLineId: Number(row?.bonusLineId ?? row?.BonusLineId ?? 0),
          bonusId: Number(row?.bonusId ?? row?.BonusId ?? 0),
          code: String(row?.code ?? row?.Code ?? ''),
          bonusItemId: itemId,
          bonusItemName: String(row?.bonusItemName ?? row?.BonusItemName ?? '').trim() || undefined,
          bonusBatchId: Number(row?.bonusBatchId ?? row?.BonusBatchId ?? 0) || undefined,
          eligibleBonusQuantity: qty,
          availableBonusStock: Number(row?.availableBonusStock ?? row?.AvailableBonusStock ?? 0),
        }
      })
      .filter((x): x is BonusPackage => x != null)
  }

  async getOrderBonusLines(orderId: number): Promise<SalesOrderBonusLineDto[]> {
    if (!orderId) return []
    const response = await apiClient.get<ApiResponse<any> | any>(
      endpoints.salesOrders.getOrderBonusLines(orderId),
      { skipHandledLocallyErrorUI: true } as any,
    )
    return toArray(response.data)
      .map((row: any): SalesOrderBonusLineDto | null => {
        const itemId = Number(row?.itemId ?? row?.ItemId ?? 0)
        const qty = Number(row?.quantity ?? row?.Quantity ?? 0)
        if (!itemId || qty <= 0) return null
        return {
          id: Number(row?.id ?? row?.Id ?? 0),
          orderId: Number(row?.orderId ?? row?.OrderId ?? orderId),
          orderLineId: Number(row?.orderLineId ?? row?.OrderLineId ?? 0),
          bonusSchemeId: Number(row?.bonusSchemeId ?? row?.BonusSchemeId ?? 0),
          itemId,
          itemName: String(row?.itemName ?? row?.ItemName ?? '').trim() || undefined,
          batchId: Number(row?.batchId ?? row?.BatchId ?? 0),
          quantity: qty,
        }
      })
      .filter((x): x is SalesOrderBonusLineDto => x != null)
  }

  private appendFormField(formData: FormData, key: string, value: unknown) {
    if (value === undefined || value === null) {
      formData.append(key, '')
      return
    }
    formData.append(key, String(value))
  }

  private buildSalesOrderLinesJson(lines: SalesOrderSaveLine[]): string {
    return JSON.stringify(lines)
  }

  private logOutboundPayload(action: 'save' | 'update', payload: SalesOrderSavePayload, salesOrderLinesJson: string) {
    const { attachmentFile, itemList, ...master } = payload
    console.log(`[SalesOrders] ${action} — master (multipart text fields)`, master)
    console.log(`[SalesOrders] ${action} — SalesOrderLinesJson (form field value)`, salesOrderLinesJson)
    console.log(`[SalesOrders] ${action} — lines (parsed)`, itemList)
    console.log(`[SalesOrders] ${action} — AttachmentFile`, attachmentFile ? { name: attachmentFile.name, size: attachmentFile.size, type: attachmentFile.type } : null)
  }

  private buildSaveUpdateFormData(payload: SalesOrderSavePayload, salesOrderLinesJson: string): FormData {
    const formData = new FormData()

    this.appendFormField(formData, 'Id', payload.id)
    this.appendFormField(formData, 'QuotCode', payload.quotCode)
    this.appendFormField(formData, 'Code', payload.code)
    this.appendFormField(formData, 'CustomerName', payload.customerName)
    this.appendFormField(formData, 'WarehouseName', payload.warehouseName)
    this.appendFormField(formData, 'Reference', payload.reference)
    this.appendFormField(formData, 'OrderDate', payload.orderDate)
    this.appendFormField(formData, 'EstDeliveryDate', payload.estDeliveryDate)
    this.appendFormField(formData, 'CustomerId', payload.customerId)
    this.appendFormField(formData, 'PaymentMethod', payload.paymentMethod)
    this.appendFormField(formData, 'TaxableAmount', payload.taxableAmount)
    this.appendFormField(formData, 'TaxAmount', payload.taxAmount)
    this.appendFormField(formData, 'Discount', payload.discount)
    this.appendFormField(formData, 'Total', payload.total)
    this.appendFormField(formData, 'SalesPersonId', payload.salesPersonId)
    this.appendFormField(formData, 'WarehouseId', payload.warehouseId)
    this.appendFormField(formData, 'PaymentTerm', payload.paymentTerm)
    this.appendFormField(formData, 'OrderStatus', payload.orderStatus)
    this.appendFormField(formData, 'IsDeliveryChallan', payload.isDeliveryChallan)
    this.appendFormField(formData, 'AttachmentPath', payload.attachmentPath)
    this.appendFormField(formData, 'CreatedDate', payload.createdDate)
    this.appendFormField(formData, 'CreatedBy', payload.createdBy)
    this.appendFormField(formData, 'ModifiedDate', payload.modifiedDate)
    this.appendFormField(formData, 'ModifiedBy', payload.modifiedBy)
    this.appendFormField(formData, 'BranchId', payload.branchId)

    this.appendFormField(formData, 'SalesOrderLinesJson', salesOrderLinesJson)

    if (payload.attachmentFile) {
      formData.append('AttachmentFile', payload.attachmentFile)
    } else {
      this.appendFormField(formData, 'AttachmentFile', '')
    }

    return formData
  }

  async save(payload: SalesOrderSavePayload): Promise<void> {
    const salesOrderLinesJson = this.buildSalesOrderLinesJson(payload.itemList)
    this.logOutboundPayload('save', payload, salesOrderLinesJson)
    const formData = this.buildSaveUpdateFormData(payload, salesOrderLinesJson)
    await apiClient.post(endpoints.salesOrders.save, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
  }

  async update(payload: SalesOrderSavePayload): Promise<void> {
    const salesOrderLinesJson = this.buildSalesOrderLinesJson(payload.itemList)
    this.logOutboundPayload('update', payload, salesOrderLinesJson)
    const formData = this.buildSaveUpdateFormData(payload, salesOrderLinesJson)
    await apiClient.post(endpoints.salesOrders.update, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
  }

  /** POST `/api/SalesOrders/ConvertToInvoice?id=` — only when the order does not require a DC. */
  async convertToInvoice(id: number): Promise<void> {
    if (!id || id <= 0) throw new Error('Invalid sales order.')
    const response = await apiClient.post(endpoints.salesOrders.convertToInvoice(id))
    this.assertResultOk(response.data, 'Convert to invoice failed.')
  }

  private assertResultOk(data: unknown, fallbackMsg: string): void {
    if (!data || typeof data !== 'object' || Array.isArray(data)) return
    const o = data as Record<string, unknown>
    const r = o.result ?? o.Result
    if (r && typeof r === 'object' && !Array.isArray(r)) {
      const ro = r as Record<string, unknown>
      if (ro.isError === true || ro.IsError === true) {
        const msg =
          (typeof ro.msg === 'string' && ro.msg.trim()) ||
          (typeof ro.Msg === 'string' && ro.Msg.trim()) ||
          fallbackMsg
        const err = new Error(msg) as Error & { response?: { data: unknown } }
        err.response = { data }
        throw err
      }
    }
  }
}

export const salesOrderService = new SalesOrderService()

